import { useState, type FormEvent } from 'react';
import { ApiError, api } from '../lib/api';
import { Button } from '../components/ui/Button';

type Mode = 'password' | 'magic';
type Status = 'idle' | 'loading' | 'sent' | 'error';

/**
 * Mappt API/Network-Fehler auf User-freundliche Messages. Login-Screen → klare
 * Diagnose ist wertvoll. Login-Endpoint gibt bewusst einen generischen 401-Text
 * ("E-Mail oder Passwort falsch.") zurück (kein Enumeration-Leak) — den zeigen
 * wir 1:1.
 */
function friendlyLoginError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) {
      const retry = err.retryAfterSeconds;
      if (retry && retry > 0) {
        const minutes = Math.ceil(retry / 60);
        return `Zu viele Versuche. Bitte in ~${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'} erneut versuchen.`;
      }
      return 'Zu viele Versuche. Bitte später erneut versuchen.';
    }
    if (err.status >= 500) {
      return 'Server-Problem. Bitte später erneut versuchen oder Admin kontaktieren.';
    }
    return err.message;
  }
  if (err instanceof Error && err.message === 'Netzwerkfehler') {
    return 'Keine Verbindung. Internet prüfen und erneut versuchen.';
  }
  return 'Login fehlgeschlagen. Bitte erneut versuchen.';
}

/**
 * Props sind optional → `<LoginPage/>` (DAM, App.tsx) verhält sich byte-identisch:
 * Titel „GREENFORCE DAM", Post-Login `window.location.assign('/')`. Der Transfer-
 * Client (Phase 2) übergibt eigenen `title` + `onPasswordSuccess` (reload(), damit
 * es path-agnostisch für Dev `/index-transfer.html` und Prod `/` funktioniert).
 */
export function LoginPage({
  title = 'GREENFORCE DAM',
  onPasswordSuccess = () => window.location.assign('/'),
}: {
  title?: string;
  onPasswordSuccess?: () => void;
} = {}) {
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState<string | null>(null);

  async function handlePasswordLogin(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !password) return;
    setStatus('loading');
    setMessage(null);
    try {
      await api('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: trimmed, password }),
      });
      // Session-Cookie ist gesetzt. Übergang aus dem unauthed-Branch via
      // injizierten Callback (DAM: assign('/'); Transfer: reload()) → der
      // /me-Check übernimmt (inkl. Forced-Change-Gate bei mustChangePassword).
      onPasswordSuccess();
    } catch (err) {
      setMessage(friendlyLoginError(err));
      setStatus('error');
    }
  }

  async function handleMagicLink(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setStatus('loading');
    setMessage(null);
    try {
      const res = await api<{ message: string }>('/api/auth/magic-link', {
        method: 'POST',
        body: JSON.stringify({ email: trimmed }),
      });
      setMessage(res.message);
      setStatus('sent');
    } catch (err) {
      setMessage(friendlyLoginError(err));
      setStatus('error');
    }
  }

  const inputClass =
    'w-full px-4 py-3 border border-ink-200 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500 focus-visible:border-transparent transition';

  function switchMode(next: Mode): void {
    setMode(next);
    setStatus('idle');
    setMessage(null);
    setPassword('');
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-brand-cream p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-md p-8">
        <h1 className="text-3xl font-semibold text-brand-green-700 mb-2 tracking-tight">
          {title}
        </h1>

        {mode === 'magic' && status === 'sent' ? (
          <div className="text-center py-4">
            <div className="text-3xl mb-2" aria-hidden="true">📬</div>
            <p className="text-brand-green-700 font-medium mb-2">Check deine Mails</p>
            <p className="text-sm text-brand-green-600">{message}</p>
            <button
              type="button"
              onClick={() => switchMode('password')}
              className="mt-6 text-xs text-brand-green-600 underline hover:text-brand-green-800"
            >
              Zurück zum Passwort-Login
            </button>
          </div>
        ) : mode === 'password' ? (
          <>
            <p className="text-sm text-brand-green-600 mb-6">
              Melde dich mit E-Mail und Passwort an.
            </p>
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@greenforce.com"
                required
                autoComplete="username"
                autoFocus
                className={inputClass}
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Passwort"
                required
                autoComplete="current-password"
                className={inputClass}
              />
              <Button type="submit" variant="primary" fullWidth loading={status === 'loading'}>
                {status === 'loading' ? 'Wird geprüft…' : 'Einloggen'}
              </Button>
              {status === 'error' && message && (
                <div
                  className="rounded-md bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
                  role="alert"
                >
                  {message}
                </div>
              )}
            </form>
            <button
              type="button"
              onClick={() => switchMode('magic')}
              className="mt-6 text-xs text-brand-green-600 underline hover:text-brand-green-800"
            >
              Stattdessen Login-Link per E-Mail anfordern
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-brand-green-600 mb-6">
              Wir schicken dir einen Login-Link per E-Mail.
            </p>
            <form onSubmit={handleMagicLink} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@greenforce.com"
                required
                autoComplete="email"
                autoFocus
                className={inputClass}
              />
              <Button type="submit" variant="primary" fullWidth loading={status === 'loading'}>
                {status === 'loading' ? 'Wird gesendet…' : 'Login-Link anfordern'}
              </Button>
              {status === 'error' && message && (
                <div
                  className="rounded-md bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
                  role="alert"
                >
                  {message}
                </div>
              )}
            </form>
            <button
              type="button"
              onClick={() => switchMode('password')}
              className="mt-6 text-xs text-brand-green-600 underline hover:text-brand-green-800"
            >
              Zurück zum Passwort-Login
            </button>
          </>
        )}

        <p className="mt-8 text-xs text-brand-green-600 text-center font-mono">
          Nur Allowlist-User. Unbekannte E-Mails erhalten dieselbe Response.
        </p>
      </div>
    </main>
  );
}
